import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail, authFileUrl } from "@/lib/apiClient";
import { PageHeader, EmptyState, money } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Calculator, Plus, Trash2, X, Upload, FileText, Pencil, Copy, Printer, ClipboardList, Image as ImageIcon } from "lucide-react";

const num = (x) => (parseFloat(x) || 0);
// Coerce every numeric field (incl. nested BOM lines & boxes) to a real number before POST,
// so an emptied input ("") never triggers a backend 422 float-parse error.
const NUMERIC_KEYS = new Set([
  "usd_rate", "wastage_pct", "wood_labour", "factory_pct", "carving", "raw_hardware", "kharad",
  "raw_profit_pct", "sanding_labour", "polish_wastage_pct", "polish_labour", "fitting_labour",
  "packing_wastage_pct", "packing_labour", "loading_labour", "shipping_container", "other_per_pc",
  "final_profit_pct", "commission_pct", "quantity", "num_boxes", "box_l", "box_b", "box_h",
  "pcs_per_hc_override", "l_ft", "w_in", "t_in", "qty", "rate", "measure", "l", "b", "h", "amount",
  "net_weight", "gross_weight",
]);
const sanitizeNums = (v) =>
  Array.isArray(v) ? v.map(sanitizeNums)
    : (v && typeof v === "object")
      ? Object.fromEntries(Object.entries(v).map(([k, val]) => [k, NUMERIC_KEYS.has(k) ? num(val) : sanitizeNums(val)]))
      : v;
const WOOD_TYPES = ["Mango", "Babool", "Sheesham", "Pine", "Oak", "Teak", "Other"];
const SHEET_ITEMS = ["MDF", "PLY", "PAPER VENEER", "MDF ONE SIDE VENEER", "MDF BOTH SIDE VENEER", "PLY ONE SIDE VENEER", "PLY BOTH SIDE VENEER"];
const empty = {
  product_name: "", sku: "", buyer_name: "", category: "Bed", range_name: "",
  wood_type: "Mango", overall_size: "", polish_type: "", product_photo: "", polish_photo: "",
  boxes: [{ l: 0, b: 0, h: 0 }], box_unit: "cm", usd_rate: 83,
  net_weight: 0, gross_weight: 0, assembly_pdf: "",
  wood_lines: [], sheet_lines: [], hardware_lines: [], polish_lines: [], packing_lines: [], final_hardware_lines: [],
  wastage_pct: 10, wood_labour: 0, factory_pct: 5, carving: 0, raw_hardware: 0, kharad: 0, raw_profit_pct: 10,
  finish_lines: [], sanding_labour: 0, polish_wastage_pct: 5, polish_labour: 0, fitting_labour: 0,
  packing_wastage_pct: 5, packing_labour: 0, loading_labour: 0,
  shipping_container: 0, other_per_pc: 0, custom_lines: [],
  final_profit_pct: 20, commission_pct: 0,
  attachments: [],
  instr_production: "", instr_sanding: "", instr_polish: "",
  instr_final_hardware: "", instr_packing: "", instr_loading: "",
};

// module-scope helpers (NOT redefined per render -> inputs keep focus)
const NumF = ({ label, value, onChange, testid }) => (
  <div className="space-y-1"><Label className="text-xs">{label}</Label>
    <Input type="number" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} /></div>
);

const PhotoField = ({ label, path, onUpload, testid }) => (
  <div className="space-y-1">
    <Label className="text-xs">{label}</Label>
    <div className="flex items-center gap-2">
      {path
        ? <img src={authFileUrl(path)} alt={label} className="h-12 w-12 rounded border border-border object-cover" />
        : <div className="flex h-12 w-12 items-center justify-center rounded border border-dashed border-border text-muted-foreground"><Upload className="h-4 w-4" /></div>}
      <label className="cursor-pointer rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted">
        {path ? "Change" : "Upload"}
        <input type="file" accept="image/*" className="hidden" data-testid={testid}
          onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])} />
      </label>
    </div>
  </div>
);

const T_IN = { type: "text", inputMode: "decimal" };
const rowQty = (it, boardft) => (num(it.l_ft) * num(it.w_in) * (boardft ? num(it.t_in) : 1) * num(it.qty)) / (boardft ? 12 : 144);
const rowTotal = (it, boardft) => rowQty(it, boardft) * num(it.rate);
const snapWoodRate = (matrix, l, t) => {
  if (!matrix) return null;
  let best = null, bd = Infinity;
  for (const k of Object.keys(matrix)) {
    const [kl, kt] = k.split("_").map(Number);
    const d = Math.abs(kl - l) + Math.abs(kt - t);
    if (d < bd) { bd = d; best = k; }
  }
  return best != null ? matrix[best] : null;
};
const snapSheetRate = (sheet, material, t) => {
  if (!sheet || !material) return null;
  const pref = material + "_";
  let best = null, bd = Infinity;
  for (const k of Object.keys(sheet)) {
    if (!k.startsWith(pref)) continue;
    const d = Math.abs(Number(k.slice(pref.length)) - t);
    if (d < bd) { bd = d; best = k; }
  }
  return best != null ? sheet[best] : null;
};
const ItemPicker = ({ items, value, onPick, testid }) => (
  <Select value={value || ""} onValueChange={(id) => onPick((items || []).find((x) => x.id === id))}>
    <SelectTrigger className="h-9" data-testid={testid}><SelectValue placeholder="Select item" /></SelectTrigger>
    <SelectContent>
      {(items || []).length === 0
        ? <div className="px-3 py-2 text-xs text-muted-foreground">No items — add in Rate Sheets</div>
        : items.map((x) => <SelectItem key={x.id} value={x.id}>{x.item_name}{x.size ? ` · ${x.size}` : ""}</SelectItem>)}
    </SelectContent>
  </Select>
);

const MaterialTable = ({ title, materials, rows, boardft, onAdd, onUp, onRm, testid }) => (
  <div>
    <div className="mb-1 flex items-center justify-between">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
      <Button type="button" size="sm" variant="outline" className="h-7 gap-1 text-xs" data-testid={`${testid}-add`} onClick={onAdd}>
        <Plus className="h-3 w-3" /> Add part</Button>
    </div>
    <div className="-mx-1 overflow-x-auto px-1"><div className="min-w-[720px] sm:min-w-0">
    <div className="grid grid-cols-12 gap-1 px-1 pb-1 text-[10px] font-semibold uppercase text-muted-foreground">
      <span className="col-span-1">Part</span><span className="col-span-2">Material</span>
      <span className="col-span-1">L ({boardft ? "ft" : "in"})</span><span className="col-span-1">B (in)</span>
      <span className="col-span-1">T ({boardft ? "in" : "mm"})</span><span className="col-span-1">Qty</span>
      <span className="col-span-1">{boardft ? "Board-ft" : "Sq-ft"}</span>
      <span className="col-span-2">Rate ₹</span><span className="col-span-1">Total</span>
    </div>
    {rows.map((it, i) => (
      <div key={i} title={it.ai_unsure ? "AI estimate — please verify" : undefined} className={`relative mb-1 grid grid-cols-12 items-center gap-1 ${it.ai_unsure ? "rounded bg-amber-50/60 ring-1 ring-amber-400" : ""}`}>
        {it.ai_unsure && <span className="absolute -right-1 -top-1.5 z-10 rounded-full bg-amber-400 px-1 text-[9px] font-bold text-amber-950">AI?</span>}
        <Input className="col-span-1" type="text" placeholder="Part" data-testid={`${testid}-part-${i}`} value={it.part} onChange={(e) => onUp(i, "part", e.target.value)} />
        <div className="col-span-2">
          <Select value={it.material} onValueChange={(v) => onUp(i, "material", v)}>
            <SelectTrigger className="h-9" data-testid={`${testid}-material-${i}`}><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>{(materials || []).map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Input className="col-span-1" {...T_IN} data-testid={`${testid}-l-${i}`} value={it.l_ft} onChange={(e) => onUp(i, "l_ft", e.target.value)} />
        <Input className="col-span-1" {...T_IN} data-testid={`${testid}-b-${i}`} value={it.w_in} onChange={(e) => onUp(i, "w_in", e.target.value)} />
        <Input className="col-span-1" {...T_IN} data-testid={`${testid}-t-${i}`} value={it.t_in} onChange={(e) => onUp(i, "t_in", e.target.value)} />
        <Input className="col-span-1" {...T_IN} data-testid={`${testid}-qty-${i}`} value={it.qty} onChange={(e) => onUp(i, "qty", e.target.value)} />
        <div className="col-span-1 truncate text-xs text-muted-foreground" data-testid={`${testid}-measure-${i}`}>{rowQty(it, boardft).toFixed(3)}</div>
        <Input className="col-span-2" {...T_IN} data-testid={`${testid}-rate-${i}`} value={it.rate} onChange={(e) => onUp(i, "rate", e.target.value)} />
        <div className="col-span-1 truncate text-xs font-semibold" data-testid={`${testid}-total-${i}`}>{money(rowTotal(it, boardft))}</div>
        <Button type="button" variant="ghost" size="icon" className="col-span-1 h-8 w-8" onClick={() => onRm(i)}><X className="h-4 w-4" /></Button>
      </div>
    ))}
    </div></div>
    <div className="mt-1 flex justify-end gap-2 border-t border-border pt-1 text-xs">
      <span className="text-muted-foreground">Subtotal</span>
      <span className="font-bold">{money(rows.reduce((s, it) => s + rowTotal(it, boardft), 0))}</span>
    </div>
  </div>
);

const StepRow = ({ label, value, strong }) => (
  <div className={`flex items-center justify-between rounded px-3 py-1.5 text-xs ${strong ? "bg-primary/10" : "bg-muted/40"}`}>
    <span className={strong ? "font-semibold uppercase tracking-wide text-primary" : "text-muted-foreground"}>{label}</span>
    <span className={strong ? "font-heading font-bold text-primary" : "font-bold"}>{money(value)}</span>
  </div>
);

const lineTotal = (it) => num(it.qty) * num(it.rate);
const ItemTable = ({ title, items, rows, onAdd, onUp, onPick, onRm, testid, catKey, onQuickAdd }) => (
  <div className="rounded border border-border p-2">
    <div className="mb-1 flex items-center justify-between">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
      <div className="flex gap-1">
        {onQuickAdd && <Button type="button" size="sm" variant="ghost" className="h-7 gap-1 text-xs text-primary" data-testid={`${testid}-new-catalog`} onClick={() => onQuickAdd(catKey)}><Plus className="h-3 w-3" /> New item</Button>}
        <Button type="button" size="sm" variant="outline" className="h-7 gap-1 text-xs" data-testid={`${testid}-add`} onClick={onAdd}>
          <Plus className="h-3 w-3" /> Add</Button>
      </div>
    </div>
    {rows.length === 0 && <p className="px-1 py-1 text-[11px] text-muted-foreground">No lines yet — tap "Add".</p>}
    <div className="hidden grid-cols-12 gap-1 px-1 pb-1 text-[10px] font-semibold uppercase text-muted-foreground sm:grid">
      <span className="col-span-4">Item</span><span className="col-span-3">Type / Size / Spec</span>
      <span className="col-span-1">Qty</span><span className="col-span-2">Rate ₹</span>
      <span className="col-span-1">Total</span><span className="col-span-1" />
    </div>
    {rows.map((it, i) => (
      <div key={i} title={it.ai_unsure ? "AI estimate — please verify" : undefined} className={`relative mb-2 grid grid-cols-2 items-end gap-2 rounded-md border border-border/60 p-2 sm:mb-1 sm:grid-cols-12 sm:items-center sm:gap-1 sm:rounded-none sm:border-0 sm:p-0 ${it.ai_unsure ? "ring-1 ring-amber-400 sm:rounded sm:bg-amber-50/60" : ""}`}>
        {it.ai_unsure && <span className="absolute -right-1 -top-1.5 z-10 rounded-full bg-amber-400 px-1 text-[9px] font-bold text-amber-950">AI?</span>}
        <div className="col-span-2 sm:col-span-4">
          <span className="mb-0.5 block text-[10px] font-semibold uppercase text-muted-foreground sm:hidden">Item</span>
          <ItemPicker items={items} value={it.catalog_id} onPick={(item) => onPick(i, item)} testid={`${testid}-pick-${i}`} />
        </div>
        <div className="col-span-2 truncate text-xs text-muted-foreground sm:col-span-3">{[it.item_type, it.size, it.other_spec].filter(Boolean).join(" · ") || "—"}</div>
        <div className="sm:col-span-1">
          <span className="mb-0.5 block text-[10px] font-semibold uppercase text-muted-foreground sm:hidden">Qty</span>
          <Input {...T_IN} data-testid={`${testid}-qty-${i}`} value={it.qty} onChange={(e) => onUp(i, "qty", e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <span className="mb-0.5 block text-[10px] font-semibold uppercase text-muted-foreground sm:hidden">Rate ₹</span>
          <Input {...T_IN} data-testid={`${testid}-rate-${i}`} value={it.rate} onChange={(e) => onUp(i, "rate", e.target.value)} />
        </div>
        <div className="truncate text-xs font-semibold sm:col-span-1">
          <span className="mb-0.5 block text-[10px] font-semibold uppercase text-muted-foreground sm:hidden">Total</span>
          {money(lineTotal(it))}
        </div>
        <Button type="button" variant="ghost" size="icon" className="col-span-2 h-8 w-full sm:col-span-1 sm:w-8" onClick={() => onRm(i)}><X className="h-4 w-4" /></Button>
      </div>
    ))}
    <div className="mt-1 flex justify-end gap-2 border-t border-border pt-1 text-xs">
      <span className="text-muted-foreground">Subtotal</span>
      <span className="font-bold" data-testid={`${testid}-subtotal`}>{money(rows.reduce((s, it) => s + lineTotal(it), 0))}</span>
    </div>
  </div>
);

function preview(f) {
  const fac = f.box_unit === "inch" ? 2.54 : 1;
  const totalCbm = (f.boxes || []).reduce((s, b) => s + num(b.l) * num(b.b) * num(b.h), 0) * (fac ** 3) / 1_000_000;
  const pcsHc = totalCbm ? Math.round(62 / totalCbm) : 0;
  let material = 0;
  f.wood_lines.forEach((i) => { material += rowTotal(i, true); });
  f.sheet_lines.forEach((i) => { material += rowTotal(i, false); });
  const hardwareCost = (f.hardware_lines || []).reduce((s, it) => s + num(it.qty) * num(it.rate), 0);
  let rt = material;
  const afterWastage = (rt = rt + rt * num(f.wastage_pct) / 100);
  const afterLabour = (rt = rt + num(f.wood_labour));
  const afterFactory = (rt = rt + rt * num(f.factory_pct) / 100);
  const afterHeads = (rt = rt + num(f.carving) + hardwareCost + num(f.kharad));
  const rawTotal = (rt = rt + rt * num(f.raw_profit_pct) / 100);
  const polishTotal = (f.polish_lines || []).reduce((s, it) => s + num(it.qty) * num(it.rate), 0);
  const packingTotal = (f.packing_lines || []).reduce((s, it) => s + num(it.qty) * num(it.rate), 0);
  const finalHwTotal = (f.final_hardware_lines || []).reduce((s, it) => s + num(it.qty) * num(it.rate), 0);
  let fin = polishTotal + packingTotal + finalHwTotal;
  fin += num(f.sanding_labour) + num(f.polish_labour) + num(f.fitting_labour) + num(f.packing_labour) + num(f.loading_labour);
  fin += polishTotal * num(f.polish_wastage_pct) / 100 + packingTotal * num(f.packing_wastage_pct) / 100;
  const ship = (pcsHc ? num(f.shipping_container) / pcsHc : 0) + num(f.other_per_pc);
  const subtotal = rawTotal + fin + ship;
  const inr = (subtotal + subtotal * num(f.final_profit_pct) / 100);
  const inrC = inr + inr * num(f.commission_pct) / 100;
  return { totalCbm: totalCbm.toFixed(4), pcsHc, rawTotal, subtotal, inr: inrC, usd: inrC / (num(f.usd_rate) || 1),
    material, hardwareCost, afterWastage, afterLabour, afterFactory, afterHeads,
    polishTotal, packingTotal, finalHwTotal, finishing: fin };
}

const INSTR_FIELDS = [["instr_production", "Production"], ["instr_sanding", "Sanding"], ["instr_polish", "Polish"], ["instr_final_hardware", "Final hardware"], ["instr_packing", "Packing"], ["instr_loading", "Loading"]];
const uniqCI = (arr) => { const m = new Map(); arr.forEach((v) => { const k = (v || "").toLowerCase(); if (!m.has(k)) m.set(k, v); }); return [...m.values()]; };
const eqCI = (a, b) => (a || "").toLowerCase() === (b || "").toLowerCase();

export default function Costing() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const initialSnapRef = useRef(null);  // clean-state snapshot for unsaved-changes guard
  const [fltBuyer, setFltBuyer] = useState("all");
  const [fltRange, setFltRange] = useState("all");
  const [instrCtx, setInstrCtx] = useState(null);
  const [f, setF] = useState(empty);
  const [rates, setRates] = useState({ catalogs: {}, wood: {}, sheet: {} });
  const [buyers, setBuyers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [quickCat, setQuickCat] = useState(null);
  const [quickForm, setQuickForm] = useState({ item_name: "", item_type: "", size: "", other_spec: "", rate: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const p = useMemo(() => preview(f), [f]);
  const cats = rates.catalogs || {};

  const load = () => api.get("/costings").then((r) => setItems(r.data));
  const loadRates = () => api.get("/rates").then((r) => setRates(r.data || { catalogs: {}, wood: {}, sheet: {} }));
  useEffect(() => { load(); loadRates(); api.get("/buyers").then((r) => setBuyers(r.data || [])); api.get("/ledgers?type=vendor").then((r) => setVendors(r.data || [])); }, []);

  const addBuyerQuick = async () => {
    const name = window.prompt("New buyer name");
    if (!name) return;
    try { await api.post("/ledgers", { name, type: "buyer" }); const r = await api.get("/buyers"); setBuyers(r.data || []); set("buyer_name", name); toast.success("Buyer added"); }
    catch (e) { toast.error("Could not add buyer"); }
  };
  const saveQuick = async () => {
    try {
      const { data: item } = await api.post("/rates/catalog-item", { catalog: quickCat, ...quickForm, rate: num(quickForm.rate) });
      await loadRates();
      const linesKey = `${quickCat}_lines`;
      setF((prev) => ({
        ...prev,
        [linesKey]: [...(prev[linesKey] || []), {
          catalog_id: item.id, item_name: item.item_name || "", item_type: item.item_type || "",
          size: item.size || "", other_spec: item.other_spec || "", qty: 1, rate: num(item.rate),
        }],
      }));
      toast.success("Added to rate sheet & plugged into a new BOM line");
      setQuickCat(null); setQuickForm({ item_name: "", item_type: "", size: "", other_spec: "", rate: "" });
    } catch (e) { toast.error("Could not add item"); }
  };

  const woodUp = (i, k, v) => setF((prev) => {
    const c = [...prev.wood_lines]; c[i] = { ...c[i], [k]: v };
    if (k !== "rate") {
      const r = c[i]; const rate = snapWoodRate(rates.wood?.[r.material], +r.l_ft || 0, +r.t_in || 0);
      if (rate != null) c[i].rate = rate;
    }
    return { ...prev, wood_lines: c };
  });
  const sheetUp = (i, k, v) => setF((prev) => {
    const c = [...prev.sheet_lines]; c[i] = { ...c[i], [k]: v };
    if (k !== "rate") {
      const r = c[i]; const rate = snapSheetRate(rates.sheet, r.material, +r.t_in || 0);
      if (rate != null) c[i].rate = rate;
    }
    return { ...prev, sheet_lines: c };
  });

  const pickInto = (key, i, item) => setF((prev) => {
    const c = [...prev[key]]; const cur = c[i] || {};
    c[i] = { ...cur, catalog_id: item?.id || "", item_name: item?.item_name || "", material: item?.item_name || "",
      item_type: item?.item_type || "", size: item?.size || "", other_spec: item?.other_spec || "",
      rate: item ? num(item.rate) : cur.rate };
    return { ...prev, [key]: c };
  });

  const upKey = (key, i, k, v) => setF((prev) => { const c = [...prev[key]]; c[i] = { ...c[i], [k]: v }; return { ...prev, [key]: c }; });
  const addKey = (key, row) => setF((prev) => ({ ...prev, [key]: [...prev[key], row] }));
  const rmKey = (key, i) => setF((prev) => ({ ...prev, [key]: prev[key].filter((_, x) => x !== i) }));

  const uploadPhoto = async (field, file) => {
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      set(field, data.path);
      toast.success("Photo uploaded");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const uploadAttachments = async (files) => {
    for (const file of files) {
      try {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/upload", fd);
        const ext = (file.name.split(".").pop() || "").toLowerCase();
        setF((prev) => ({ ...prev, attachments: [...(prev.attachments || []), { name: file.name, path: data.path, ext }] }));
      } catch (e) { toast.error(`Could not upload ${file.name}`); }
    }
    toast.success("Attachment(s) uploaded");
  };
  const removeAttachment = (i) => setF((prev) => ({ ...prev, attachments: (prev.attachments || []).filter((_, x) => x !== i) }));

  const save = async () => {
    const zeroLines = [...f.wood_lines, ...f.sheet_lines, ...f.hardware_lines, ...f.polish_lines, ...f.packing_lines, ...f.final_hardware_lines]
      .filter((it) => (it.catalog_id || it.item_name || it.material) && !num(it.rate));
    if (zeroLines.length && !window.confirm(`${zeroLines.length} BOM line(s) have a ₹0 rate (item picked but no rate). Save anyway?`)) { toast.info("Save cancelled"); return; }
    try {
      if (editId) await api.put(`/costings/${editId}`, sanitizeNums(f));
      else await api.post("/costings", sanitizeNums(f));
      toast.success(editId ? "Cost sheet updated" : "Cost sheet saved");
      initialSnapRef.current = null; setOpen(false); setEditId(null); setF(empty); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openEdit = (c) => { const nf = { ...empty, ...c }; setEditId(c.id); setF(nf); initialSnapRef.current = JSON.stringify(nf); setOpen(true); };
  const cloneFrom = async (c) => {
    const nf = { ...empty, ...c, product_name: `${c.product_name} (Copy)`, sku: "" };
    delete nf.id; delete nf.created_at; delete nf.created_by;
    setEditId(null); setF(nf); initialSnapRef.current = JSON.stringify(nf); setOpen(true);
    try {
      const r = await api.get("/costings/next-sku");
      setF((prev) => ({ ...prev, sku: r.data.sku }));
      initialSnapRef.current = JSON.stringify({ ...nf, sku: r.data.sku });
    } catch (e) { console.warn("next-sku unavailable, keeping blank SKU", e); }
  };
  const remove = async (id) => { await api.delete(`/costings/${id}`); toast.success("Deleted"); load(); };

  const printInstructions = (c) => {
    if (!c) return;
    const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;");
    const rows = INSTR_FIELDS.map(([k, l]) => `<h4>${l}</h4><p>${esc((c[k] || "").trim() || "—")}</p>`).join("");
    const html = `<html><head><meta charset="utf-8"><title>${esc(c.product_name)} — Instructions</title><style>body{font-family:system-ui,Arial,sans-serif;padding:32px;color:#1a1a1a}h2{margin:0 0 2px}h4{margin:16px 0 2px;text-transform:uppercase;font-size:11px;letter-spacing:.08em;color:#777}p{margin:0;white-space:pre-wrap;font-size:14px;line-height:1.4}</style></head><body><h2>${esc(c.product_name)}</h2><p style="color:#777;font-size:12px">${esc(c.sku || "")}</p>${rows}</body></html>`;
    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    const w = window.open(url, "_blank", "width=720,height=900");
    if (!w) { URL.revokeObjectURL(url); toast.error("Allow pop-ups to print instructions"); return; }
    w.addEventListener("load", () => { w.focus(); w.print(); URL.revokeObjectURL(url); });
  };
  const buyerOpts = useMemo(() => uniqCI(items.map((c) => c.buyer_name).filter(Boolean)), [items]);
  const rangeOpts = useMemo(() => uniqCI(items.map((c) => c.range_name).filter(Boolean)), [items]);
  const shown = useMemo(() => items.filter((c) => (fltBuyer === "all" || eqCI(c.buyer_name, fltBuyer)) && (fltRange === "all" || eqCI(c.range_name, fltRange))), [items, fltBuyer, fltRange]);

  const downloadPdf = async (c) => {
    try {
      const res = await api.get(`/costings/${c.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `${(c.product_name || "cost-sheet").replace(/\s+/g, "_")}_costing.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not generate PDF"); }
  };
  const [orderCtx, setOrderCtx] = useState(null);
  const [oQty, setOQty] = useState(1);
  const [oInt, setOInt] = useState(true);
  const [oExt, setOExt] = useState(false);
  const [oSupplier, setOSupplier] = useState("");
  const genOrders = async () => {
    try {
      const { data } = await api.post(`/costings/${orderCtx.id}/generate-orders`, { quantity: num(oQty) || 1, internal: oInt, external: oExt, supplier_id: oSupplier });
      toast.success(data.count ? `${data.count} order(s) created: ${data.created.join(", ")}` : "No lines to order");
      setOrderCtx(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8">
      <PageHeader title="Product Costing" icon={Calculator}
        subtitle="Full cost sheet — photos, boxes/CBM, wood BOM, finishing & margins. Feeds product name, SKU & price to other modules."
        action={
          <Dialog open={open} onOpenChange={(v) => {
            if (!v) {
              if (initialSnapRef.current !== null && JSON.stringify(f) !== initialSnapRef.current
                  && !window.confirm("You have unsaved changes. Discard them?")) return;
              setOpen(false); setEditId(null); initialSnapRef.current = null;
            } else {
              setOpen(true);
              if (!editId) {
                setF(empty); initialSnapRef.current = JSON.stringify(empty);
                api.get("/costings/next-sku").then((r) => {
                  setF((prev) => ({ ...prev, sku: r.data.sku }));
                  initialSnapRef.current = JSON.stringify({ ...empty, sku: r.data.sku });
                });
              }
            }
          }}>
            <DialogTrigger asChild><Button data-testid="add-costing-button" className="gap-2"><Plus className="h-4 w-4" /> New Cost Sheet</Button></DialogTrigger>
            <DialogContent className="max-w-3xl p-4 sm:p-6">
              <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Cost Sheet" : "New Product Cost Sheet"}</DialogTitle></DialogHeader>
              <div className="max-h-[74vh] space-y-5 overflow-y-auto pr-1">
                <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div className="col-span-2 space-y-1"><Label className="text-xs">Product name</Label>
                    <Input data-testid="cost-product-name" value={f.product_name} onChange={(e) => set("product_name", e.target.value)} /></div>
                  <div className="space-y-1"><Label className="text-xs">SKU / Code</Label>
                    <Input data-testid="cost-sku" value={f.sku} onChange={(e) => set("sku", e.target.value)} /></div>
                  <div className="space-y-1"><Label className="text-xs">Buyer</Label>
                    <Select value={buyers.find((b) => b.name === f.buyer_name)?.id || ""}
                      onValueChange={(v) => { const b = buyers.find((x) => x.id === v); if (b) set("buyer_name", b.name); }}>
                      <SelectTrigger className="h-9" data-testid="cost-buyer"><SelectValue placeholder="Select buyer" /></SelectTrigger>
                      <SelectContent>{buyers.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button type="button" size="sm" variant="ghost" className="mt-1 h-6 gap-1 px-1 text-[11px] text-primary" data-testid="cost-buyer-new" onClick={addBuyerQuick}><Plus className="h-3 w-3" /> New buyer</Button>
                    <Input className="mt-1" value={f.buyer_name} onChange={(e) => set("buyer_name", e.target.value)} placeholder="or type a name" /></div>
                  <div className="space-y-1"><Label className="text-xs">Category</Label>
                    <Input value={f.category} onChange={(e) => set("category", e.target.value)} /></div>
                  <div className="space-y-1"><Label className="text-xs">Range</Label>
                    <Input value={f.range_name} onChange={(e) => set("range_name", e.target.value)} /></div>
                  <div className="space-y-1"><Label className="text-xs">Overall size (L×B×H)</Label>
                    <Input data-testid="cost-overall" value={f.overall_size} onChange={(e) => set("overall_size", e.target.value)} placeholder="120×60×75 cm" /></div>
                  <div className="space-y-1"><Label className="text-xs">Polish type</Label>
                    <Input value={f.polish_type} onChange={(e) => set("polish_type", e.target.value)} placeholder="AA01" /></div>
                  <NumF label="USD rate" value={f.usd_rate} onChange={(v) => set("usd_rate", v)} />
                  <PhotoField label="Product photo" path={f.product_photo} testid="cost-product-photo" onUpload={(file) => uploadPhoto("product_photo", file)} />
                  <PhotoField label="Polish colour photo" path={f.polish_photo} testid="cost-polish-photo" onUpload={(file) => uploadPhoto("polish_photo", file)} />
                </section>

                <section className="rounded-md border border-border p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="font-heading text-sm font-bold">Boxes & CBM</p>
                    <div className="flex items-center gap-2">
                      <Select value={f.box_unit} onValueChange={(v) => set("box_unit", v)}>
                        <SelectTrigger className="h-8 w-24" data-testid="box-unit"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="cm">cm</SelectItem><SelectItem value="inch">inch</SelectItem></SelectContent>
                      </Select>
                      <Button type="button" size="sm" variant="outline" className="h-7 gap-1 text-xs" data-testid="box-add"
                        onClick={() => addKey("boxes", { l: 0, b: 0, h: 0 })}><Plus className="h-3 w-3" /> Add box</Button>
                    </div>
                  </div>
                  <div className="-mx-1 overflow-x-auto px-1"><div className="min-w-[440px] sm:min-w-0">
                  <div className="grid grid-cols-12 gap-1 px-1 pb-1 text-[10px] font-semibold uppercase text-muted-foreground">
                    <span className="col-span-3">Box</span><span className="col-span-3">Length</span>
                    <span className="col-span-3">Breadth</span><span className="col-span-2">Height</span><span className="col-span-1" />
                  </div>
                  {f.boxes.map((b, i) => (
                    <div key={i} className="mb-1 grid grid-cols-12 items-center gap-1">
                      <span className="col-span-3 text-xs text-muted-foreground">Box {i + 1}</span>
                      <Input className="col-span-3" type="text" inputMode="decimal" value={b.l} onChange={(e) => upKey("boxes", i, "l", e.target.value)} />
                      <Input className="col-span-3" type="text" inputMode="decimal" value={b.b} onChange={(e) => upKey("boxes", i, "b", e.target.value)} />
                      <Input className="col-span-2" type="text" inputMode="decimal" value={b.h} onChange={(e) => upKey("boxes", i, "h", e.target.value)} />
                      <Button type="button" variant="ghost" size="icon" className="col-span-1 h-8 w-8" onClick={() => rmKey("boxes", i)}><X className="h-4 w-4" /></Button>
                    </div>
                  ))}
                  </div></div>
                  <p className="mt-1 text-xs text-muted-foreground">Total CBM: <b>{p.totalCbm}</b> · Pcs / 40'HC: <b>{p.pcsHc}</b></p>
                  <div className="mt-3 grid grid-cols-2 gap-3 border-t border-border pt-3 sm:grid-cols-3">
                    <NumF label="Net weight / unit (kg)" value={f.net_weight} onChange={(v) => set("net_weight", v)} testid="cost-net-weight" />
                    <NumF label="Gross weight / unit (kg)" value={f.gross_weight} onChange={(v) => set("gross_weight", v)} testid="cost-gross-weight" />
                    <div className="space-y-1">
                      <Label className="text-xs">Assembly instructions (PDF)</Label>
                      {f.assembly_pdf ? (
                        <div className="flex items-center gap-2">
                          <a href={authFileUrl(f.assembly_pdf)} target="_blank" rel="noreferrer" data-testid="cost-assembly-link"
                            className="flex items-center gap-1 truncate text-xs text-primary hover:underline"><FileText className="h-3.5 w-3.5" /> View PDF</a>
                          <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => set("assembly_pdf", "")}><X className="h-3.5 w-3.5" /></Button>
                        </div>
                      ) : (
                        <label className="flex cursor-pointer items-center gap-1 rounded-md border border-border px-2 py-1.5 text-xs font-medium hover:bg-muted">
                          <Upload className="h-3.5 w-3.5" /> Upload PDF
                          <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid="cost-assembly-upload"
                            onChange={(e) => e.target.files[0] && uploadPhoto("assembly_pdf", e.target.files[0])} />
                        </label>
                      )}
                    </div>
                  </div>
                </section>

                <section className="space-y-3 rounded-md border border-border p-3">
                  <p className="font-heading text-sm font-bold">1 · Material BOM</p>
                  <MaterialTable title="Solid wood" materials={WOOD_TYPES} rows={f.wood_lines} boardft testid="wood_lines"
                    onAdd={() => addKey("wood_lines", { part: "", material: "", l_ft: 1, w_in: 0, t_in: 1, qty: 1, rate: 0 })}
                    onUp={woodUp} onRm={(i) => rmKey("wood_lines", i)} />
                  <MaterialTable title="MDF/ply" materials={SHEET_ITEMS} rows={f.sheet_lines} testid="sheet_lines"
                    onAdd={() => addKey("sheet_lines", { part: "", material: "", l_ft: 0, w_in: 0, t_in: 0.5, qty: 1, rate: 0 })}
                    onUp={sheetUp} onRm={(i) => rmKey("sheet_lines", i)} />
                  <div className="mt-1 flex justify-end gap-3 border-t-2 border-border pt-2 text-sm">
                    <span className="font-semibold text-muted-foreground">Grand total (Solid wood + MDF/ply)</span>
                    <span className="font-bold" data-testid="bom-grand-total">{money(
                      f.wood_lines.reduce((s, it) => s + rowTotal(it, true), 0)
                      + f.sheet_lines.reduce((s, it) => s + rowTotal(it, false), 0)
                    )}</span>
                  </div>
                </section>

                <section className="space-y-2 rounded-md border border-border p-3">
                  <p className="mb-1 font-heading text-sm font-bold">2 · Raw costing (running total after each step)</p>
                  <StepRow label="Material grand total (Solid wood + MDF/ply)" value={p.material} />
                  <div className="grid grid-cols-2 gap-3">
                    <NumF label="Wastage %" value={f.wastage_pct} onChange={(v) => set("wastage_pct", v)} />
                    <NumF label="Wood labour ₹" value={f.wood_labour} onChange={(v) => set("wood_labour", v)} />
                  </div>
                  <StepRow label="After wastage %" value={p.afterWastage} />
                  <StepRow label="After wood labour" value={p.afterLabour} />
                  <div className="grid grid-cols-2 gap-3">
                    <NumF label="Factory %" value={f.factory_pct} onChange={(v) => set("factory_pct", v)} />
                    <div />
                  </div>
                  <StepRow label="After factory %" value={p.afterFactory} />
                  <ItemTable title="Raw hardware" items={cats.hardware} rows={f.hardware_lines} testid="hardware_lines" catKey="hardware" onQuickAdd={setQuickCat}
                    onAdd={() => addKey("hardware_lines", { catalog_id: "", item_name: "", item_type: "", size: "", other_spec: "", qty: 1, rate: 0 })}
                    onUp={(i, k, v) => upKey("hardware_lines", i, k, v)} onPick={(i, item) => pickInto("hardware_lines", i, item)} onRm={(i) => rmKey("hardware_lines", i)} />
                  <div className="grid grid-cols-2 gap-3">
                    <NumF label="Carving/CNC ₹" value={f.carving} onChange={(v) => set("carving", v)} />
                    <NumF label="Kharad ₹" value={f.kharad} onChange={(v) => set("kharad", v)} />
                  </div>
                  <StepRow label="After carving + hardware + kharad" value={p.afterHeads} />
                  <div className="grid grid-cols-2 gap-3">
                    <NumF label="Raw profit %" value={f.raw_profit_pct} onChange={(v) => set("raw_profit_pct", v)} />
                    <div className="flex items-end"><div className="w-full"><StepRow label="Raw total" value={p.rawTotal} strong /></div></div>
                  </div>
                </section>

                <section className="space-y-3 rounded-md border border-border p-3">
                  <p className="font-heading text-sm font-bold">3 · Finishing</p>
                  <ItemTable title="Polish materials" items={cats.polish} rows={f.polish_lines} testid="polish_lines" catKey="polish" onQuickAdd={setQuickCat}
                    onAdd={() => addKey("polish_lines", { catalog_id: "", item_name: "", item_type: "", size: "", other_spec: "", qty: 1, rate: 0 })}
                    onUp={(i, k, v) => upKey("polish_lines", i, k, v)} onPick={(i, item) => pickInto("polish_lines", i, item)} onRm={(i) => rmKey("polish_lines", i)} />
                  <ItemTable title="Packing materials" items={cats.packing} rows={f.packing_lines} testid="packing_lines" catKey="packing" onQuickAdd={setQuickCat}
                    onAdd={() => addKey("packing_lines", { catalog_id: "", item_name: "", item_type: "", size: "", other_spec: "", qty: 1, rate: 0 })}
                    onUp={(i, k, v) => upKey("packing_lines", i, k, v)} onPick={(i, item) => pickInto("packing_lines", i, item)} onRm={(i) => rmKey("packing_lines", i)} />
                  <ItemTable title="Final hardware" items={cats.final_hardware} rows={f.final_hardware_lines} testid="final_hardware_lines" catKey="final_hardware" onQuickAdd={setQuickCat}
                    onAdd={() => addKey("final_hardware_lines", { catalog_id: "", item_name: "", item_type: "", size: "", other_spec: "", qty: 1, rate: 0 })}
                    onUp={(i, k, v) => upKey("final_hardware_lines", i, k, v)} onPick={(i, item) => pickInto("final_hardware_lines", i, item)} onRm={(i) => rmKey("final_hardware_lines", i)} />
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <NumF label="Sanding labour ₹" value={f.sanding_labour} onChange={(v) => set("sanding_labour", v)} />
                    <NumF label="Polish wastage %" value={f.polish_wastage_pct} onChange={(v) => set("polish_wastage_pct", v)} />
                    <NumF label="Polish labour ₹" value={f.polish_labour} onChange={(v) => set("polish_labour", v)} />
                    <NumF label="Fitting labour ₹" value={f.fitting_labour} onChange={(v) => set("fitting_labour", v)} />
                    <NumF label="Packing wastage %" value={f.packing_wastage_pct} onChange={(v) => set("packing_wastage_pct", v)} />
                    <NumF label="Packing labour ₹" value={f.packing_labour} onChange={(v) => set("packing_labour", v)} />
                    <NumF label="Loading labour ₹" value={f.loading_labour} onChange={(v) => set("loading_labour", v)} />
                  </div>
                  <StepRow label="Finishing total" value={p.finishing} strong />
                </section>

                <section className="rounded-md border border-border p-3">
                  <p className="mb-2 font-heading text-sm font-bold">4 · Shipping & margin</p>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <NumF label="Shipping / container ₹" value={f.shipping_container} onChange={(v) => set("shipping_container", v)} />
                    <NumF label="Other / pc ₹" value={f.other_per_pc} onChange={(v) => set("other_per_pc", v)} />
                    <NumF label="Final profit %" value={f.final_profit_pct} onChange={(v) => set("final_profit_pct", v)} />
                    <NumF label="Commission %" value={f.commission_pct} onChange={(v) => set("commission_pct", v)} />
                  </div>
                </section>

                <section className="space-y-3 rounded-md border border-border p-3">
                  <div className="flex items-center justify-between">
                    <p className="font-heading text-sm font-bold">5 · Attachments (drawings, docs)</p>
                    <label className="cursor-pointer rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted">
                      <Upload className="mr-1 inline h-3.5 w-3.5" /> Add files
                      <input type="file" multiple className="hidden" data-testid="cost-attachments"
                        onChange={(e) => e.target.files.length && uploadAttachments([...e.target.files])} />
                    </label>
                  </div>
                  {(f.attachments || []).length === 0
                    ? <p className="text-xs text-muted-foreground">No attachments yet. Upload drawings, spec sheets or reference docs.</p>
                    : <div className="space-y-1">
                        {f.attachments.map((a, i) => (
                          <div key={i} className="flex items-center justify-between rounded border border-border px-2 py-1 text-xs" data-testid={`cost-attachment-${i}`}>
                            <a href={authFileUrl(a.path)} target="_blank" rel="noreferrer" className="flex items-center gap-1 truncate text-primary hover:underline">
                              <FileText className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{a.name}</span></a>
                            <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeAttachment(i)}><X className="h-3.5 w-3.5" /></Button>
                          </div>
                        ))}
                      </div>}
                </section>

                <section className="space-y-3 rounded-md border border-border p-3">
                  <p className="font-heading text-sm font-bold">6 · Special instructions</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {[["instr_production", "Production"], ["instr_sanding", "Sanding"], ["instr_polish", "Polish"],
                      ["instr_final_hardware", "Final hardware"], ["instr_packing", "Packing"], ["instr_loading", "Loading"]].map(([k, l]) => (
                      <div key={k} className="space-y-1">
                        <Label className="text-xs">{l}</Label>
                        <Textarea rows={2} value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`cost-${k}`} placeholder={`${l} instructions…`} />
                      </div>
                    ))}
                  </div>
                </section>

                <div className="grid grid-cols-3 gap-3 rounded-md bg-muted/40 p-3 text-sm">
                  <div><p className="text-xs text-muted-foreground">Raw total</p><p className="font-bold">{money(p.rawTotal)}</p></div>
                  <div><p className="text-xs text-muted-foreground">Subtotal</p><p className="font-bold">{money(p.subtotal)}</p></div>
                  <div><p className="text-xs text-muted-foreground">Final ₹</p><p className="font-heading font-bold text-primary">{money(p.inr)}</p></div>
                </div>
              </div>
              <DialogFooter><Button data-testid="costing-save" onClick={save} disabled={!f.product_name}>{editId ? "Update Cost Sheet" : "Save Cost Sheet"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        } />

      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <Select value={fltBuyer} onValueChange={setFltBuyer}>
            <SelectTrigger className="w-full sm:w-52" data-testid="costing-filter-buyer"><SelectValue placeholder="All buyers" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All buyers</SelectItem>{buyerOpts.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={fltRange} onValueChange={setFltRange}>
            <SelectTrigger className="w-full sm:w-52" data-testid="costing-filter-range"><SelectValue placeholder="All ranges" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All ranges</SelectItem>{rangeOpts.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
          {(fltBuyer !== "all" || fltRange !== "all") && <Button variant="ghost" size="sm" data-testid="costing-filter-clear" onClick={() => { setFltBuyer("all"); setFltRange("all"); }}>Clear</Button>}
        </div>
      )}
      {items.length === 0 ? (
        <EmptyState icon={Calculator} title="No cost sheets yet" hint="Create a cost sheet to compute the final product price." />
      ) : shown.length === 0 ? (
        <EmptyState icon={Calculator} title="No matches" hint="No cost sheets match the selected buyer / range." />
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {shown.map((c) => (
            <Card key={c.id} data-testid={`costing-card-${c.id}`} className="animate-fade-up rounded-md border-border p-5 shadow-none">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted/40" data-testid={`costing-photo-${c.id}`}>
                    {c.product_photo
                      ? <img src={authFileUrl(c.product_photo)} alt={c.product_name} className="h-full w-full object-cover" />
                      : <ImageIcon className="h-6 w-6 text-muted-foreground/40" />}
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold">{c.product_name}</h3>
                    <p className="text-xs text-muted-foreground">{c.sku || "—"} · {c.category || "—"} {c.buyer_name && `· ${c.buyer_name}`}</p>
                    {c.range_name && <p className="text-[11px] text-muted-foreground">Range: {c.range_name}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" data-testid={`costing-clone-${c.id}`} onClick={() => cloneFrom(c)}
                    className="h-8 w-8 text-muted-foreground hover:text-primary" title="Duplicate"><Copy className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" data-testid={`costing-edit-${c.id}`} onClick={() => openEdit(c)}
                    className="h-8 w-8 text-muted-foreground hover:text-primary" title="Edit"><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" data-testid={`costing-delete-${c.id}`} onClick={() => remove(c.id)}
                    className="h-8 w-8 text-muted-foreground hover:text-destructive" title="Delete"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                <Row label="Wood cost" v={c.wood_cost} /><Row label="Raw total" v={c.raw_total} />
                <Row label="Finishing" v={c.finishing_total} /><Row label="Shipping/pc" v={c.shipping_per_pc} />
                <Row label="Total CBM" v={c.total_cbm} plain /><Row label="Pcs/40'HC" v={c.pcs_per_hc} plain />
              </div>
              <div className="mt-3 flex items-center justify-between rounded-md bg-primary/10 px-3 py-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-primary">Final price</span>
                <span className="font-heading text-lg font-bold text-primary">{money(c.final_price_inr)} <span className="text-xs font-normal text-muted-foreground">/ ${c.final_price_usd}</span></span>
              </div>
              {(c.attachments || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`costing-attachments-${c.id}`}>
                  {c.attachments.map((a, i) => (
                    <a key={i} href={authFileUrl(a.path)} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px] text-primary hover:bg-muted">
                      <FileText className="h-3 w-3" /> <span className="max-w-[140px] truncate">{a.name}</span></a>
                  ))}
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="gap-1 text-xs" data-testid={`costing-pdf-${c.id}`} onClick={() => downloadPdf(c)}><FileText className="h-3.5 w-3.5" /> PDF</Button>
                <Button size="sm" variant="outline" className="gap-1 text-xs" data-testid={`costing-instructions-${c.id}`} onClick={() => setInstrCtx(c)}><ClipboardList className="h-3.5 w-3.5" /> Instructions</Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!orderCtx} onOpenChange={(v) => !v && setOrderCtx(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Generate Orders — {orderCtx?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1"><Label className="text-xs">Production quantity (pieces)</Label>
              <Input type="text" inputMode="decimal" value={oQty} onChange={(e) => setOQty(e.target.value)} data-testid="orders-qty" /></div>
            <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={oInt} onChange={(e) => setOInt(e.target.checked)} data-testid="orders-internal" />
              <span>Internal store issue orders <span className="text-muted-foreground">(Raw wood, Hardware, Polish, Packing stores)</span></span></label>
            <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={oExt} onChange={(e) => setOExt(e.target.checked)} data-testid="orders-external" />
              <span>External supplier purchase orders <span className="text-muted-foreground">(buy raw materials from suppliers)</span></span></label>
            {oExt && (
              <div className="space-y-1 pl-6">
                <Label className="text-xs">Supplier (for GST & address on PO)</Label>
                <Select value={oSupplier} onValueChange={setOSupplier}>
                  <SelectTrigger className="h-9" data-testid="orders-supplier"><SelectValue placeholder="Select supplier (optional)" /></SelectTrigger>
                  <SelectContent>{vendors.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <p className="text-xs text-muted-foreground">Each order's quantities = per-piece BOM × production quantity; rates come from this cost sheet. Orders appear under Sales Orders.</p>
          </div>
          <DialogFooter><Button onClick={genOrders} disabled={!oInt && !oExt} data-testid="orders-generate">Create Orders</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!quickCat} onOpenChange={(v) => !v && setQuickCat(null)}>
        <DialogContent className="max-w-md p-4 sm:p-6">
          <DialogHeader><DialogTitle className="font-heading">Add to rate sheet — {quickCat}</DialogTitle></DialogHeader>
          <p className="-mt-2 text-xs text-muted-foreground">Saves to the rate sheet and drops it in as a new BOM line here with this rate.</p>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-xs">Item name</Label>
              <Input data-testid="quick-item-name" value={quickForm.item_name} onChange={(e) => setQuickForm({ ...quickForm, item_name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1"><Label className="text-xs">Type</Label><Input value={quickForm.item_type} onChange={(e) => setQuickForm({ ...quickForm, item_type: e.target.value })} /></div>
              <div className="space-y-1"><Label className="text-xs">Size</Label><Input value={quickForm.size} onChange={(e) => setQuickForm({ ...quickForm, size: e.target.value })} /></div>
              <div className="space-y-1"><Label className="text-xs">Other spec</Label><Input value={quickForm.other_spec} onChange={(e) => setQuickForm({ ...quickForm, other_spec: e.target.value })} /></div>
              <div className="space-y-1"><Label className="text-xs">Rate ₹</Label><Input type="text" inputMode="decimal" data-testid="quick-item-rate" value={quickForm.rate} onChange={(e) => setQuickForm({ ...quickForm, rate: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter><Button data-testid="quick-item-save" onClick={saveQuick} disabled={!quickForm.item_name}>Add &amp; use in BOM</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!instrCtx} onOpenChange={(v) => !v && setInstrCtx(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Instructions — {instrCtx?.product_name}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
            {INSTR_FIELDS.map(([k, l]) => (
              <div key={k} data-testid={`instr-${k}`}>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{l}</p>
                <p className="whitespace-pre-wrap text-sm">{(instrCtx?.[k] || "").trim() || "—"}</p>
              </div>
            ))}
          </div>
          <DialogFooter><Button data-testid="instructions-print" onClick={() => printInstructions(instrCtx)} className="gap-1"><Printer className="h-4 w-4" /> Print</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ label, v, plain }) => (
  <div className="flex items-center justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium">{plain ? v : money(v)}</span>
  </div>
);
